import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Per-session visitor id, stable across page navigations for the
 * lifetime of the localStorage. No PII — just a random opaque token
 * so the backend can dedupe unique sessions for the admin analytics.
 */
const VISITOR_KEY = "aldakheel_visitor_id";

function getVisitorId() {
  if (typeof window === "undefined") return "ssr";
  let id = window.localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = `v-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    try {
      window.localStorage.setItem(VISITOR_KEY, id);
    } catch {
      /* localStorage disabled — fall back to ephemeral id */
    }
  }
  return id;
}

/**
 * Fire-and-forget ping on every route change. Failures are silent —
 * analytics must never break the user flow. Skipped on /admin and
 * /portal so internal navigation doesn't pollute the visitor count.
 *
 * A ref-based dedupe guards against React 18 StrictMode double-effects
 * in dev and against accidental remounts so we never log the same
 * (session, path) twice.
 */
export default function VisitorTracker() {
  const { pathname } = useLocation();
  const { lang } = useI18n();
  const lastPingRef = useRef("");

  useEffect(() => {
    if (
      pathname.startsWith("/admin") ||
      pathname.startsWith("/portal")
    ) {
      return;
    }
    const session_id = getVisitorId();
    const key = `${session_id}|${pathname}`;
    if (lastPingRef.current === key) return;
    lastPingRef.current = key;
    // Send asynchronously; don't await.
    api
      .post("/track/visit", { session_id, path: pathname, language: lang })
      .catch(() => {});
  }, [pathname, lang]);

  return null;
}
